// Grupos de Estudo (peer-led, multi-owner, cross-org).
// [[project-study-groups]]
//
// Views:
//   - StudyGroupsView: lista grupos do aluno + modal "Criar grupo" + lista de
//     convites pendentes recebidos (com Aceitar/Recusar inline).
//   - StudyGroupDetailView: header + tabs Repositório (árvore de pastas/itens)
//     + Membros. Owner: pode adicionar pasta/banco e convidar; member: read-only.
//
// Estado global:
//   - activeStudyGroupId: signal pra abrir a view de detalhe inline.
//   - openStudyGroup(id) / closeStudyGroup() helpers.

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent
} from "@dnd-kit/core";
import {
  Users,
  Plus,
  FolderPlus,
  Database,
  MoreVertical,
  Trash2,
  Edit3,
  UserPlus,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Mail,
  Crown,
  X,
  Check,
  Folder,
  FolderOpen,
  PlayCircle,
  Bell,
  Home,
  FileText,
  StickyNote,
  Eye,
  EyeOff,
  BookOpen,
  ClipboardCheck
} from "lucide-react";
import { Button } from "@app/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "@app/ui/components/card";
import { Badge } from "@app/ui/components/badge";
import { Skeleton } from "@app/ui/components/skeleton";
import { call, ApiError } from "./api";
import { OrganizeToggle } from "./OrganizeToggle";
import { friendlyError } from "@app/ui/lib/friendly-error";
import { openAlert, openConfirm, openPrompt } from "./ConfirmPromptModal";

// ============================================================================
// Tipos
// ============================================================================

export interface StudyGroupRow {
  id: string;
  name: string;
  description: string | null;
  coverColor: string | null;
  coverIcon: string | null;
  role: "owner" | "member";
  memberCount: number;
  itemCount: number;
  folderCount: number;
  joinedAt: string;
  createdAt: string;
  archivedAt: string | null;
}

export interface StudyGroupDetail {
  id: string;
  name: string;
  description: string | null;
  coverColor: string | null;
  coverIcon: string | null;
  hideMemberInfo: boolean;
  /** "owners" (default) — só proprietários veem convites pendentes
   *  "all" — qualquer membro vê. [[project-study-groups-pending-invites]] */
  pendingInvitesVisibility: "owners" | "all";
  createdBy: string | null;
  createdByName: string | null;
  createdAt: string;
  archivedAt: string | null;
  viewerRole: "owner" | "member";
}

export interface StudyGroupPendingInvite {
  id: string;
  inviteeUserId: string;
  inviteeName: string;
  /** Só vem pra owners — pra membros normais vem null (anti-enumeração). */
  inviteeEmail: string | null;
  invitedByName: string | null;
  message: string | null;
  createdAt: string;
}

export interface StudyGroupItem {
  id: string;
  parentId: string | null;
  kind: "folder" | "bank_ref" | "file" | "note_ref" | "exam_ref";
  name: string;
  bankId: string | null;
  bankMeta: {
    name: string;
    version: number;
    questionCount: number;
    estudo?: {
      respondidas: number;
      acertos: number;
      accuracyPct: number | null;
    };
    provas?: {
      total: number;
      melhorPercentual: number | null;
    };
  } | null;
  fileUrl: string | null;
  fileSizeBytes: number | null;
  fileMime: string | null;
  noteId: string | null;
  examId: string | null;
  position: number;
  addedBy: string | null;
  addedAt: string;
  updatedAt: string;
}

export interface StudyGroupMember {
  userId: string;
  name: string;
  email: string | null;
  role: "owner" | "member";
  joinedAt: string;
  invitedBy: string | null;
  connectionStatus: "pending_sent" | "pending_received" | "accepted" | null;
  isSelf: boolean;
  hiddenForMembers: boolean;
}

export interface StudyGroupInvite {
  id: string;
  groupId: string;
  groupName: string;
  groupDescription: string | null;
  coverColor: string | null;
  coverIcon: string | null;
  invitedByName: string | null;
  message: string | null;
  createdAt: string;
}

// ============================================================================
// Signals globais
// ============================================================================

export const studyGroupsList = signal<StudyGroupRow[]>([]);
export const studyGroupsPhase = signal<"idle" | "loading" | "ready" | "error">(
  "idle"
);
export const studyGroupInvites = signal<StudyGroupInvite[]>([]);
export const activeStudyGroupId = signal<string | null>(null);

export function openStudyGroup(id: string): void {
  activeStudyGroupId.value = id;
}

export function closeStudyGroup(): void {
  activeStudyGroupId.value = null;
}

export async function loadStudyGroups(opts: { silent?: boolean } = {}) {
  if (!opts.silent) studyGroupsPhase.value = "loading";
  try {
    const [groupsRes, invitesRes] = await Promise.all([
      call<{ groups: StudyGroupRow[] }>("/api/student/study-groups"),
      call<{ received: StudyGroupInvite[] }>(
        "/api/student/study-group-invites"
      )
    ]);
    studyGroupsList.value = groupsRes.groups;
    studyGroupInvites.value = invitesRes.received;
    studyGroupsPhase.value = "ready";
  } catch (err) {
    console.warn("[study-groups] load falhou:", err);
    studyGroupsPhase.value = "error";
  }
}

// ============================================================================
// View: lista + criar
// ============================================================================

/** Card compacto pra Home: mostra resumo dos grupos do aluno + convites
 *  pendentes destacados. Lazy-load via loadStudyGroups quando montado. */
export function GruposEstudoHomeCard({
  onOpen,
  onOpenGroup
}: {
  onOpen: () => void;
  onOpenGroup: (id: string) => void;
}) {
  useEffect(() => {
    if (studyGroupsPhase.value === "idle") void loadStudyGroups({ silent: true });
  }, []);
  const groups = studyGroupsList.value;
  const invites = studyGroupInvites.value;
  const phase = studyGroupsPhase.value;
  const totalGroups = groups.length;
  const totalItems = groups.reduce((s, g) => s + g.itemCount, 0);

  return (
    <section class="rounded-xl border border-border bg-card p-5">
      <div class="mb-3 flex items-start justify-between gap-3">
        <div class="flex items-center gap-2.5">
          <span class="grid size-9 place-items-center rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-600 text-white shadow-sm">
            <Users className="size-4" />
          </span>
          <div>
            <h2 class="text-base font-semibold text-foreground">
              Grupos de Estudo
            </h2>
            <p class="text-[11px] text-muted-foreground">
              {phase === "loading"
                ? "Carregando…"
                : totalGroups === 0
                  ? "Crie um grupo pra estudar com colegas"
                  : `${totalGroups} ${totalGroups === 1 ? "grupo" : "grupos"} · ${totalItems} ${totalItems === 1 ? "item" : "itens"}`}
            </p>
          </div>
        </div>
        <button
          type="button"
          title="Ver todos os grupos"
          aria-label="Ver todos os grupos"
          onClick={onOpen}
          class="text-xs font-medium text-violet-600 hover:underline dark:text-violet-300"
        >
          Ver todos
        </button>
      </div>

      {invites.length > 0 && (
        <button
          type="button"
          title="Ver convites pendentes"
          aria-label="Ver convites pendentes"
          onClick={onOpen}
          class="mb-3 flex w-full items-center gap-2 rounded-lg border border-violet-500/40 bg-violet-500/10 px-3 py-2 text-left transition hover:bg-violet-500/15"
        >
          <Mail className="size-4 text-violet-600 dark:text-violet-300" />
          <span class="flex-1 text-xs">
            <strong class="font-semibold text-foreground">
              {invites.length}{" "}
              {invites.length === 1 ? "convite pendente" : "convites pendentes"}
            </strong>
            <span class="text-muted-foreground"> — clique pra responder</span>
          </span>
          <ChevronRight className="size-3.5 text-muted-foreground" />
        </button>
      )}

      {phase === "ready" && groups.length === 0 && invites.length === 0 && (
        <button
          type="button"
          title="Criar primeiro grupo de estudo"
          aria-label="Criar primeiro grupo de estudo"
          onClick={onOpen}
          class="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-border bg-card/40 py-4 text-xs text-muted-foreground transition hover:border-violet-500/40 hover:text-violet-600"
        >
          <Plus className="size-3.5" />
          Criar primeiro grupo
        </button>
      )}

      {groups.length > 0 && (
        <div class="space-y-2">
          {groups.slice(0, 3).map((g) => (
            <button
              key={g.id}
              type="button"
              title={`Abrir ${g.name}`}
              aria-label={`Abrir grupo ${g.name}`}
              onClick={() => onOpenGroup(g.id)}
              class="flex w-full items-center gap-2.5 rounded-lg border border-border bg-card/60 p-2 text-left transition hover:border-violet-500/40 hover:bg-accent"
            >
              <span class="grid size-9 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-violet-100 to-fuchsia-100 text-xs font-bold text-violet-700 dark:from-violet-800 dark:to-fuchsia-800 dark:text-white">
                {groupInitials(g.name)}
              </span>
              <div class="min-w-0 flex-1">
                <div class="truncate text-sm font-medium">{g.name}</div>
                <div class="text-[11px] text-muted-foreground">
                  {g.memberCount}{" "}
                  {g.memberCount === 1 ? "membro" : "membros"} · {g.itemCount}{" "}
                  {g.itemCount === 1 ? "item" : "itens"}
                </div>
              </div>
              {g.role === "owner" && (
                <Crown className="size-3.5 text-violet-500" />
              )}
            </button>
          ))}
          {groups.length > 3 && (
            <button
              type="button"
              title="Ver todos os grupos"
              aria-label="Ver todos os grupos"
              onClick={onOpen}
              class="w-full rounded-lg py-1.5 text-center text-xs text-muted-foreground hover:text-violet-600"
            >
              +{groups.length - 3} {groups.length - 3 === 1 ? "grupo" : "grupos"}
            </button>
          )}
        </div>
      )}
    </section>
  );
}

export function StudyGroupsView() {
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (studyGroupsPhase.value === "idle") void loadStudyGroups();
  }, []);

  // Se há grupo ativo, renderiza o detalhe inline
  if (activeStudyGroupId.value) {
    return <StudyGroupDetailView />;
  }

  const phase = studyGroupsPhase.value;
  const groups = studyGroupsList.value;
  const invites = studyGroupInvites.value;

  return (
    <div class="space-y-6">
      <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex items-start gap-3">
          <span class="grid size-10 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-600 text-white shadow-md">
            <Users className="size-5" />
          </span>
          <div>
            <h1 class="text-2xl font-semibold text-foreground">
              Grupos de Estudo
            </h1>
            <p class="mt-1 text-sm text-muted-foreground">
              Organize-se com colegas: criem pastas, compartilhem bancos,
              recebam notificações quando alguém adicionar conteúdo novo.
            </p>
          </div>
        </div>
        <Button
          onClick={() => setCreating(true)}
          class="bg-violet-600 hover:bg-violet-700 text-white"
        >
          <Plus className="mr-2 size-4" />
          Novo grupo
        </Button>
      </div>

      {invites.length > 0 && (
        <Card class="border-violet-500/40 bg-violet-500/5">
          <CardHeader>
            <CardTitle class="flex items-center gap-2 text-base">
              <Mail className="size-4 text-violet-500" />
              Convites pendentes ({invites.length})
            </CardTitle>
            <CardDescription>
              Alguém te convidou pra entrar em um grupo. Aceite pra ter acesso
              ao repositório.
            </CardDescription>
          </CardHeader>
          <CardContent class="space-y-3">
            {invites.map((inv) => (
              <InviteRow key={inv.id} invite={inv} />
            ))}
          </CardContent>
        </Card>
      )}

      {phase === "loading" && (
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Skeleton class="h-32 w-full" />
          <Skeleton class="h-32 w-full" />
          <Skeleton class="h-32 w-full" />
        </div>
      )}

      {phase === "error" && (
        <Card>
          <CardContent class="py-8 text-center text-sm text-muted-foreground">
            Não consegui carregar seus grupos. Tente recarregar a página.
          </CardContent>
        </Card>
      )}

      {phase === "ready" && groups.length === 0 && (
        <Card>
          <CardContent class="py-12 text-center">
            <Users className="mx-auto size-12 text-muted-foreground/50" />
            <h2 class="mt-4 text-base font-medium">
              Você ainda não tem grupos de estudo
            </h2>
            <p class="mt-2 text-sm text-muted-foreground">
              Crie um pra reunir seus colegas e compartilhar conteúdo.
            </p>
            <Button
              onClick={() => setCreating(true)}
              class="mt-4 bg-violet-600 hover:bg-violet-700 text-white"
            >
              <Plus className="mr-2 size-4" />
              Criar primeiro grupo
            </Button>
          </CardContent>
        </Card>
      )}

      {phase === "ready" && groups.length > 0 && (
        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {groups.map((g) => (
            <GroupCard key={g.id} group={g} />
          ))}
        </div>
      )}

      {creating && (
        <CreateGroupModal
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            void loadStudyGroups({ silent: true });
            openStudyGroup(id);
          }}
        />
      )}
    </div>
  );
}

function GroupCard({ group }: { group: StudyGroupRow }) {
  const initials = groupInitials(group.name);
  const isOwner = group.role === "owner";
  return (
    <button
      type="button"
      title={`Abrir ${group.name}`}
      aria-label={`Abrir grupo ${group.name}`}
      onClick={() => openStudyGroup(group.id)}
      class="group relative flex flex-col rounded-xl border border-border bg-card text-left transition hover:-translate-y-0.5 hover:border-violet-500/60 hover:shadow-lg"
    >
      {/* Faixa colorida superior (gradient violeta com sutil padrão).
          rounded-t-xl + overflow-hidden APENAS na faixa pra não clipar o avatar
          que sai por cima dela. */}
      <div class="relative h-16 overflow-hidden rounded-t-xl bg-gradient-to-br from-violet-500 via-violet-600 to-fuchsia-600">
        <div class="absolute inset-0 opacity-20 [background-image:radial-gradient(circle_at_20%_30%,white_0,transparent_40%),radial-gradient(circle_at_80%_70%,white_0,transparent_35%)]" />
        {isOwner && (
          <span class="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-black/30 px-2 py-0.5 text-[10px] font-medium text-white backdrop-blur">
            <Crown className="size-2.5" />
            Proprietário
          </span>
        )}
      </div>
      {/* Avatar redondo flutuando sobre a faixa. z-10 garante que fica POR CIMA
          e relative pra criar contexto de empilhamento. */}
      <div class="relative z-10 -mt-8 ml-4 grid size-16 shrink-0 place-items-center rounded-2xl border-4 border-card bg-gradient-to-br from-violet-100 to-fuchsia-100 text-lg font-bold text-violet-700 shadow-md dark:from-violet-800 dark:to-fuchsia-800 dark:text-white">
        {initials}
      </div>
      <div class="flex flex-1 flex-col gap-2 px-4 pb-4 pt-3">
        <div>
          <h3 class="line-clamp-1 text-base font-semibold text-foreground">
            {group.name}
          </h3>
          {group.description && (
            <p class="mt-0.5 line-clamp-2 text-xs leading-relaxed text-muted-foreground">
              {group.description}
            </p>
          )}
        </div>
        <div class="mt-auto flex items-center gap-3 border-t border-border pt-2 text-[11px] text-muted-foreground">
          <span class="inline-flex items-center gap-1">
            <Users className="size-3" />
            <strong class="font-semibold text-foreground">
              {group.memberCount}
            </strong>
            {group.memberCount === 1 ? "membro" : "membros"}
          </span>
          <span class="inline-flex items-center gap-1">
            <Database className="size-3" />
            <strong class="font-semibold text-foreground">
              {group.itemCount}
            </strong>
            {group.itemCount === 1 ? "item" : "itens"}
          </span>
          <ChevronRight class="ml-auto size-4 text-muted-foreground/40 transition group-hover:translate-x-0.5 group-hover:text-violet-500" />
        </div>
      </div>
    </button>
  );
}

/** Gera iniciais a partir do nome do grupo.
 *  - "MED Unicid Karol" → "MK"
 *  - "MED Unicid Turma 35" → "M35" (incorpora número inteiro)
 *  - "Anatomia 2026" → "A26" (número >3 dígitos = últimos 2)
 *  - "Cardiologia" → "CA" (palavra única, 2 primeiras letras)
 */
function groupInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  const numTokens = words.filter((w) => /^\d+$/.test(w));
  const alphaTokens = words.filter((w) => /[a-zA-ZÀ-ÿ]/.test(w));
  if (numTokens.length > 0 && alphaTokens.length > 0) {
    const firstLetter = alphaTokens[0][0].toUpperCase();
    const num = numTokens[numTokens.length - 1];
    const numPart = num.length <= 3 ? num : num.slice(-2);
    return `${firstLetter}${numPart}`;
  }
  if (alphaTokens.length === 1) return alphaTokens[0].slice(0, 2).toUpperCase();
  if (alphaTokens.length >= 2) {
    return (
      alphaTokens[0][0] + alphaTokens[alphaTokens.length - 1][0]
    ).toUpperCase();
  }
  // Só números ou caracteres especiais
  return words[0].slice(0, 2).toUpperCase();
}

function InviteRow({ invite }: { invite: StudyGroupInvite }) {
  const [busy, setBusy] = useState<"accept" | "decline" | null>(null);

  async function respond(action: "accept" | "decline") {
    setBusy(action);
    try {
      const res = await call<{ ok: boolean; groupId?: string }>(
        `/api/student/study-group-invites?id=${encodeURIComponent(invite.id)}&action=${action}`,
        { method: "PATCH" }
      );
      await loadStudyGroups({ silent: true });
      if (action === "accept" && res.groupId) {
        openStudyGroup(res.groupId);
      }
    } catch (err) {
      void openAlert({
        title: "Não consegui responder",
        message: friendlyError(
          err instanceof ApiError ? err : new Error(String(err))
        )
      });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div class="flex flex-col gap-3 rounded-lg border border-border bg-card p-3 sm:flex-row sm:items-center sm:justify-between">
      <div class="min-w-0 flex-1">
        <div class="font-medium text-foreground">{invite.groupName}</div>
        <div class="text-xs text-muted-foreground">
          {invite.invitedByName
            ? `Convite de ${invite.invitedByName}`
            : "Convite recebido"}
          {invite.message ? ` · "${invite.message}"` : ""}
        </div>
      </div>
      <div class="flex shrink-0 gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={busy !== null}
          onClick={() => void respond("decline")}
        >
          {busy === "decline" ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <X className="size-3.5" />
          )}
          Recusar
        </Button>
        <Button
          size="sm"
          class="bg-violet-600 hover:bg-violet-700 text-white"
          disabled={busy !== null}
          onClick={() => void respond("accept")}
        >
          {busy === "accept" ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <Check className="size-3.5" />
          )}
          Aceitar
        </Button>
      </div>
    </div>
  );
}

function CreateGroupModal({
  onClose,
  onCreated
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (!name.trim()) {
      void openAlert({
        title: "Nome obrigatório",
        message: "Dê um nome ao grupo."
      });
      return;
    }
    setSaving(true);
    try {
      const res = await call<{ group: { id: string } }>(
        "/api/student/study-groups",
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            name: name.trim(),
            description: description.trim() || null
          })
        }
      );
      onCreated(res.group.id);
    } catch (err) {
      void openAlert({
        title: "Não consegui criar o grupo",
        message: friendlyError(
          err instanceof ApiError ? err : new Error(String(err))
        )
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      class="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm sm:items-center"
      onClick={onClose}
    >
      <div
        class="w-full max-w-md rounded-t-2xl bg-card p-6 shadow-xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div class="mb-4 flex items-center justify-between">
          <h2 class="text-lg font-semibold">Novo grupo de estudo</h2>
          <button
            onClick={onClose}
            class="text-muted-foreground hover:text-foreground"
          >
            <X className="size-5" />
          </button>
        </div>
        <div class="space-y-4">
          <div>
            <label class="mb-1 block text-sm font-medium">Nome</label>
            <input
              type="text"
              maxLength={80}
              value={name}
              onInput={(e) => setName((e.target as HTMLInputElement).value)}
              placeholder="MED Unicid 2026"
              class="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-violet-500 focus:outline-none"
              autoFocus
            />
          </div>
          <div>
            <label class="mb-1 block text-sm font-medium">
              Descrição (opcional)
            </label>
            <textarea
              maxLength={500}
              rows={3}
              value={description}
              onInput={(e) =>
                setDescription((e.target as HTMLTextAreaElement).value)
              }
              placeholder="Grupo de estudo para a turma de medicina"
              class="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-violet-500 focus:outline-none"
            />
          </div>
        </div>
        <div class="mt-6 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            class="bg-violet-600 hover:bg-violet-700 text-white"
            onClick={() => void submit()}
            disabled={saving || !name.trim()}
          >
            {saving ? (
              <Loader2 className="mr-2 size-4 animate-spin" />
            ) : (
              <Plus className="mr-2 size-4" />
            )}
            Criar grupo
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// View: detalhe do grupo
// ============================================================================

const detailSignal = signal<StudyGroupDetail | null>(null);
const itemsSignal = signal<StudyGroupItem[]>([]);
const membersSignal = signal<StudyGroupMember[]>([]);
const detailPhase = signal<"idle" | "loading" | "ready" | "error">("idle");
const activeTab = signal<"repo" | "members">("repo");
const expandedFolders = signal<Set<string>>(new Set());
const pendingInvitesSignal = signal<StudyGroupPendingInvite[]>([]);
/** True = viewer pode cancelar convites (= owner). UI esconde botão pra membros
 *  comuns mesmo quando podem ver a lista (visibility='all'). */
const pendingInvitesCanCancel = signal<boolean>(false);

async function loadDetail(id: string) {
  detailPhase.value = "loading";
  try {
    const [g, repo, members] = await Promise.all([
      call<{ group: StudyGroupDetail }>(
        `/api/student/study-groups?id=${encodeURIComponent(id)}`
      ),
      call<{ viewerRole: string; items: StudyGroupItem[] }>(
        `/api/student/study-group-repo?groupId=${encodeURIComponent(id)}`
      ),
      call<{ members: StudyGroupMember[] }>(
        `/api/student/study-group-members?groupId=${encodeURIComponent(id)}`
      )
    ]);
    detailSignal.value = g.group;
    itemsSignal.value = repo.items;
    membersSignal.value = members.members;
    detailPhase.value = "ready";
    // Pending invites: carrega em separado (pode dar 403 pra membros não-owner
    // em grupos com pendingInvitesVisibility='owners' — esperado, falha
    // silenciosa e deixa o array vazio). [[project-study-groups-pending-invites]]
    try {
      const pending = await call<{
        canCancel: boolean;
        pending: StudyGroupPendingInvite[];
      }>(`/api/student/study-group-invites?groupId=${encodeURIComponent(id)}`);
      pendingInvitesSignal.value = pending.pending;
      pendingInvitesCanCancel.value = pending.canCancel;
    } catch {
      pendingInvitesSignal.value = [];
      pendingInvitesCanCancel.value = false;
    }
  } catch (err) {
    console.warn("[study-group-detail] load falhou:", err);
    detailPhase.value = "error";
  }
}

function StudyGroupDetailView() {
  const id = activeStudyGroupId.value;

  useEffect(() => {
    if (id) void loadDetail(id);
    return () => {
      detailSignal.value = null;
      itemsSignal.value = [];
      membersSignal.value = [];
      detailPhase.value = "idle";
      activeTab.value = "repo";
      selectedFolderId.value = null;
      expandedFolders.value = new Set();
    };
  }, [id]);

  if (!id) return null;

  const phase = detailPhase.value;
  const detail = detailSignal.value;

  return (
    <div class="space-y-4">
      <div class="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => closeStudyGroup()}
          class="text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="mr-1 size-4" />
          Grupos
        </Button>
      </div>

      {phase === "loading" && (
        <div class="space-y-4">
          <Skeleton class="h-20 w-full" />
          <Skeleton class="h-64 w-full" />
        </div>
      )}

      {phase === "error" && (
        <Card>
          <CardContent class="py-8 text-center text-sm text-muted-foreground">
            Não consegui carregar este grupo. Pode ter sido apagado ou você
            perdeu o acesso.
          </CardContent>
        </Card>
      )}

      {phase === "ready" && detail && (
        <>
          <DetailHeader detail={detail} />
          <TabNav />
          {activeTab.value === "repo" && (
            <RepoTab detail={detail} groupId={id} />
          )}
          {activeTab.value === "members" && (
            <MembersTab detail={detail} groupId={id} />
          )}
        </>
      )}
    </div>
  );
}

function DetailHeader({ detail }: { detail: StudyGroupDetail }) {
  const isOwner = detail.viewerRole === "owner";
  const members = membersSignal.value;
  const items = itemsSignal.value;
  const bankCount = items.filter((i) => i.kind === "bank_ref").length;
  const fileCount = items.filter((i) => i.kind === "file").length;
  const folderCount = items.filter((i) => i.kind === "folder").length;
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<"name" | "description" | null>(null);
  const initials = groupInitials(detail.name);

  return (
    <>
      <div class="rounded-xl border border-border bg-card">
        {/* Faixa colorida superior. overflow-hidden + rounded-t-xl SÓ na faixa
            pra não clipar o avatar que sai por cima dela. */}
        <div class="relative h-24 overflow-hidden rounded-t-xl bg-gradient-to-br from-violet-500 via-violet-600 to-fuchsia-600">
          <div class="absolute inset-0 opacity-20 [background-image:radial-gradient(circle_at_15%_25%,white_0,transparent_45%),radial-gradient(circle_at_85%_70%,white_0,transparent_40%)]" />
          {/* Ações no topo direito */}
          <div class="absolute right-3 top-3 flex gap-2">
            {isOwner && (
              <button
                type="button"
                title="Convidar pessoa"
                aria-label="Convidar pessoa"
                onClick={() => setInviting(true)}
                class="inline-flex items-center gap-1.5 rounded-md bg-white/95 px-3 py-1.5 text-xs font-medium text-violet-700 shadow-sm transition hover:bg-white"
              >
                <UserPlus className="size-3.5" />
                Convidar
              </button>
            )}
          </div>
        </div>

        <div class="relative px-5 pb-4">
          {/* Avatar flutuando sobre a faixa (z-10 + relative pra ficar visível). */}
          <div class="relative z-10 -mt-10 mb-3 flex items-end justify-between gap-3">
            <div class="grid size-20 shrink-0 place-items-center rounded-2xl border-4 border-card bg-gradient-to-br from-violet-100 to-fuchsia-100 text-2xl font-bold text-violet-700 shadow-md dark:from-violet-800 dark:to-fuchsia-800 dark:text-white">
              {initials}
            </div>
            {isOwner && (
              <span class="mb-1 inline-flex items-center gap-1 rounded-full bg-violet-500/10 px-2.5 py-1 text-[11px] font-medium text-violet-700 dark:text-violet-300">
                <Crown className="size-3" />
                Você é proprietário
              </span>
            )}
          </div>

          {/* Nome (editável inline pra owners) */}
          <div class="flex items-start gap-2">
            <div class="min-w-0 flex-1">
              <h1 class="truncate text-xl font-semibold leading-tight text-foreground">
                {detail.name}
              </h1>
              {detail.description && (
                <p class="mt-1 line-clamp-2 text-sm leading-relaxed text-muted-foreground">
                  {detail.description}
                </p>
              )}
              {detail.createdByName && (
                <p class="mt-1 text-[11px] text-muted-foreground/70">
                  criado por {detail.createdByName}
                </p>
              )}
            </div>
            {isOwner && (
              <button
                type="button"
                title="Renomear grupo"
                aria-label="Renomear grupo"
                onClick={() => setEditing("name")}
                class="shrink-0 rounded-md border border-border bg-card px-2 py-1.5 text-xs text-muted-foreground transition hover:border-violet-500/60 hover:text-violet-600"
              >
                <Edit3 className="size-3.5" />
              </button>
            )}
          </div>

          {/* Stats horizontais */}
          <div class="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-border pt-3 text-xs">
            <StatChip
              icon={<Users className="size-3.5 text-violet-500" />}
              label={members.length === 1 ? "membro" : "membros"}
              value={members.length}
            />
            <StatChip
              icon={<Database className="size-3.5 text-violet-500" />}
              label={bankCount === 1 ? "banco" : "bancos"}
              value={bankCount}
            />
            {folderCount > 0 && (
              <StatChip
                icon={<Folder className="size-3.5 text-amber-500" />}
                label={folderCount === 1 ? "pasta" : "pastas"}
                value={folderCount}
              />
            )}
            {fileCount > 0 && (
              <StatChip
                icon={<FolderPlus className="size-3.5 text-blue-500" />}
                label={fileCount === 1 ? "arquivo" : "arquivos"}
                value={fileCount}
              />
            )}

            {/* Avatars sobrepostos dos primeiros 5 membros */}
            {members.length > 0 && (
              <div class="ml-auto flex items-center -space-x-2">
                {members.slice(0, 5).map((m) => (
                  <div
                    key={m.userId}
                    title={m.name}
                    aria-label={m.name}
                    class={`grid size-7 place-items-center rounded-full border-2 border-card text-[10px] font-bold text-white shadow-sm ${avatarBg(m.userId)}`}
                  >
                    {memberInitials(m.name)}
                  </div>
                ))}
                {members.length > 5 && (
                  <div class="grid size-7 place-items-center rounded-full border-2 border-card bg-muted text-[10px] font-semibold text-muted-foreground shadow-sm">
                    +{members.length - 5}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {inviting && (
        <InviteModal
          groupId={detail.id}
          onClose={() => setInviting(false)}
          onInvited={() => {
            setInviting(false);
            void loadDetail(detail.id);
          }}
        />
      )}
      {editing && (
        <EditGroupModal
          detail={detail}
          field={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void loadDetail(detail.id);
            void loadStudyGroups({ silent: true });
          }}
        />
      )}
    </>
  );
}

function StatChip({
  icon,
  label,
  value
}: {
  icon: any;
  label: string;
  value: number | string;
}) {
  return (
    <span class="inline-flex items-center gap-1.5 text-muted-foreground">
      {icon}
      <strong class="font-semibold text-foreground">{value}</strong>
      {label}
    </span>
  );
}

function memberInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function avatarBg(userId: string): string {
  // Cor estável por hash simples do userId.
  const palette = [
    "bg-violet-500",
    "bg-fuchsia-500",
    "bg-pink-500",
    "bg-rose-500",
    "bg-orange-500",
    "bg-amber-500",
    "bg-emerald-500",
    "bg-teal-500",
    "bg-cyan-500",
    "bg-blue-500",
    "bg-indigo-500"
  ];
  let h = 0;
  for (let i = 0; i < userId.length; i++) h = (h * 31 + userId.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
}

/** Modal pra editar nome ou descrição do grupo. Só owner. */
function EditGroupModal({
  detail,
  field,
  onClose,
  onSaved
}: {
  detail: StudyGroupDetail;
  field: "name" | "description";
  onClose: () => void;
  onSaved: () => void;
}) {
  const [value, setValue] = useState(
    field === "name" ? detail.name : detail.description ?? ""
  );
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (field === "name" && !value.trim()) return;
    setSaving(true);
    try {
      await call(
        `/api/student/study-groups?id=${encodeURIComponent(detail.id)}`,
        {
          method: "PATCH",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(
            field === "name"
              ? { name: value.trim() }
              : { description: value.trim() || null }
          )
        }
      );
      onSaved();
    } catch (err) {
      void openAlert({
        title: "Não consegui salvar",
        message: friendlyError(
          err instanceof ApiError ? err : new Error(String(err))
        )
      });
    } finally {
      setSaving(false);
    }
  }

  const title = field === "name" ? "Renomear grupo" : "Editar descrição";
  return (
    <div
      class="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm sm:items-center"
      onClick={onClose}
    >
      <div
        class="w-full max-w-md rounded-t-2xl bg-card p-6 shadow-xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div class="mb-4 flex items-center justify-between">
          <h2 class="text-lg font-semibold">{title}</h2>
          <button
            type="button"
            title="Fechar"
            aria-label="Fechar"
            onClick={onClose}
            class="text-muted-foreground hover:text-foreground"
          >
            <X className="size-5" />
          </button>
        </div>
        {field === "name" ? (
          <input
            type="text"
            maxLength={80}
            value={value}
            onInput={(e) => setValue((e.target as HTMLInputElement).value)}
            class="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-violet-500 focus:outline-none"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") void submit();
            }}
          />
        ) : (
          <textarea
            maxLength={500}
            rows={3}
            value={value}
            onInput={(e) =>
              setValue((e.target as HTMLTextAreaElement).value)
            }
            class="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-violet-500 focus:outline-none"
            autoFocus
          />
        )}
        <div class="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            class="bg-violet-600 hover:bg-violet-700 text-white"
            onClick={() => void submit()}
            disabled={saving || (field === "name" && !value.trim())}
          >
            {saving ? (
              <Loader2 className="mr-2 size-4 animate-spin" />
            ) : (
              <Check className="mr-2 size-4" />
            )}
            Salvar
          </Button>
        </div>
      </div>
    </div>
  );
}

function TabNav() {
  const memberCount = membersSignal.value.length;
  const itemCount = itemsSignal.value.length;
  return (
    <div class="flex gap-2 border-b border-border">
      <TabButton
        active={activeTab.value === "repo"}
        onClick={() => (activeTab.value = "repo")}
      >
        <FolderOpen className="size-4" />
        Repositório
        {itemCount > 0 && (
          <span class="ml-1 rounded-full bg-muted px-1.5 text-[10px] font-semibold text-muted-foreground">
            {itemCount}
          </span>
        )}
      </TabButton>
      <TabButton
        active={activeTab.value === "members"}
        onClick={() => (activeTab.value = "members")}
      >
        <Users className="size-4" />
        Membros
        {memberCount > 0 && (
          <span class="ml-1 rounded-full bg-muted px-1.5 text-[10px] font-semibold text-muted-foreground">
            {memberCount}
          </span>
        )}
      </TabButton>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children
}: {
  active: boolean;
  onClick: () => void;
  children: any;
}) {
  return (
    <button
      onClick={onClick}
      class={`inline-flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium transition ${
        active
          ? "border-violet-500 text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}

// ============================================================================
// Tab: Repositório
// ============================================================================

/** Pasta selecionada na tree aside. null = raiz ("Tudo"). */
const selectedFolderId = signal<string | null>(null);

// Modo organizar do repositório do grupo (só relevante pra owner). Arrasto
// desligado por padrão pra não atrapalhar o scroll por toque no mobile; não
// persiste, abre sempre "seguro". [[feedback Luís 2026-05-27]]
const groupRepoEditMode = signal(false);

function RepoTab({
  detail,
  groupId
}: {
  detail: StudyGroupDetail;
  groupId: string;
}) {
  const items = itemsSignal.value;
  const isOwner = detail.viewerRole === "owner";
  const [adding, setAdding] = useState<"folder" | "bank" | null>(null);
  const [dragLabel, setDragLabel] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  // Mapas auxiliares
  const byParent = new Map<string | null, StudyGroupItem[]>();
  for (const it of items) {
    const arr = byParent.get(it.parentId) ?? [];
    arr.push(it);
    byParent.set(it.parentId, arr);
  }
  const folders = items.filter((i) => i.kind === "folder");
  const byId = new Map(items.map((i) => [i.id, i]));

  // Descendentes de uma pasta (pra cycle detection no drop)
  function isDescendant(folderId: string, possibleAncestor: string): boolean {
    let cur: string | null = folderId;
    const seen = new Set<string>();
    while (cur) {
      if (cur === possibleAncestor) return true;
      if (seen.has(cur)) return false;
      seen.add(cur);
      cur = byId.get(cur)?.parentId ?? null;
    }
    return false;
  }

  async function onDragEnd(e: DragEndEvent) {
    setDragLabel(null);
    if (!isOwner) return;
    const aId = String(e.active?.id ?? "");
    const oId = e.over?.id ? String(e.over.id) : null;
    if (!aId.startsWith("item:") || !oId) return;
    const itemId = aId.slice("item:".length);
    let targetParentId: string | null;
    if (oId === "folder:root") {
      targetParentId = null;
    } else if (oId.startsWith("folder:")) {
      targetParentId = oId.slice("folder:".length);
    } else {
      return;
    }
    const dragging = byId.get(itemId);
    if (!dragging) return;
    if ((dragging.parentId ?? null) === targetParentId) return;
    // Cycle: pasta não pode entrar em descendente dela mesma
    if (
      dragging.kind === "folder" &&
      targetParentId &&
      isDescendant(targetParentId, itemId)
    ) {
      void openAlert({
        title: "Movimento inválido",
        message: "Não dá pra mover uma pasta pra dentro dela mesma."
      });
      return;
    }
    try {
      await call(
        `/api/student/study-group-repo?id=${encodeURIComponent(itemId)}`,
        {
          method: "PATCH",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ parentId: targetParentId })
        }
      );
      // Se moveu pra uma pasta nova, seleciona ela
      if (targetParentId) selectedFolderId.value = targetParentId;
      void loadDetail(groupId);
    } catch (err) {
      void openAlert({
        title: "Não consegui mover",
        message: friendlyError(
          err instanceof ApiError ? err : new Error(String(err))
        )
      });
    }
  }

  function onDragStart(e: DragStartEvent) {
    const aId = String(e.active?.id ?? "");
    if (!aId.startsWith("item:")) return;
    const it = byId.get(aId.slice("item:".length));
    setDragLabel(it?.name ?? null);
  }

  return (
    <div class="space-y-3">
      {isOwner && items.length > 0 && (
        <div class="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            class="bg-violet-600 hover:bg-violet-700 text-white"
            onClick={() => setAdding("bank")}
          >
            <Database className="mr-2 size-4" />
            Adicionar banco
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setAdding("folder")}
          >
            <FolderPlus className="mr-2 size-4" />
            Nova pasta
          </Button>
          <div class="ml-auto">
            <OrganizeToggle
              active={groupRepoEditMode.value}
              onToggle={() =>
                (groupRepoEditMode.value = !groupRepoEditMode.value)
              }
            />
          </div>
        </div>
      )}
      {isOwner && items.length > 0 && groupRepoEditMode.value && (
        <div class="rounded-md border border-violet-500/40 bg-violet-500/10 px-3 py-2 text-[11px] text-violet-700 dark:text-violet-300">
          Modo organizar ativo: arraste bancos e pastas pra reorganizar. Toque em
          <strong> Concluir</strong> pra voltar a deslizar a tela normalmente.
        </div>
      )}

      {items.length === 0 ? (
        isOwner ? (
          <div class="rounded-xl border border-dashed border-border bg-card/40 p-8">
            <div class="mx-auto max-w-md text-center">
              <div class="mx-auto mb-3 grid size-14 place-items-center rounded-2xl bg-gradient-to-br from-violet-100 to-fuchsia-100 dark:from-violet-800 dark:to-fuchsia-800">
                <FolderOpen className="size-7 text-violet-600 dark:text-violet-300" />
              </div>
              <h3 class="text-base font-semibold text-foreground">
                Repositório vazio
              </h3>
              <p class="mt-1 text-sm text-muted-foreground">
                Adicione bancos do seu acervo ou crie pastas pra organizar o
                conteúdo do grupo.
              </p>
            </div>
            <div class="mx-auto mt-5 grid max-w-lg gap-3 sm:grid-cols-2">
              <button
                type="button"
                title="Adicionar um banco de questões"
                aria-label="Adicionar um banco de questões"
                onClick={() => setAdding("bank")}
                class="group flex flex-col items-start gap-2 rounded-lg border border-border bg-card p-4 text-left transition hover:-translate-y-0.5 hover:border-violet-500/60 hover:shadow-md"
              >
                <span class="grid size-9 place-items-center rounded-lg bg-violet-500/10 text-violet-600 transition group-hover:bg-violet-500 group-hover:text-white dark:text-violet-300">
                  <Database className="size-5" />
                </span>
                <span class="text-sm font-semibold text-foreground">
                  Adicionar banco
                </span>
                <span class="text-xs text-muted-foreground">
                  Pesque no seu acervo ou na Biblioteca pública.
                </span>
              </button>
              <button
                type="button"
                title="Criar uma pasta"
                aria-label="Criar uma pasta"
                onClick={() => setAdding("folder")}
                class="group flex flex-col items-start gap-2 rounded-lg border border-border bg-card p-4 text-left transition hover:-translate-y-0.5 hover:border-amber-500/60 hover:shadow-md"
              >
                <span class="grid size-9 place-items-center rounded-lg bg-amber-500/10 text-amber-600 transition group-hover:bg-amber-500 group-hover:text-white dark:text-amber-300">
                  <FolderPlus className="size-5" />
                </span>
                <span class="text-sm font-semibold text-foreground">
                  Nova pasta
                </span>
                <span class="text-xs text-muted-foreground">
                  Organize por tema, disciplina ou prova.
                </span>
              </button>
            </div>
          </div>
        ) : (
          <div class="rounded-xl border border-dashed border-border bg-card/40 p-8 text-center">
            <FolderOpen className="mx-auto mb-2 size-10 text-muted-foreground/40" />
            <h3 class="text-sm font-medium text-foreground">
              Aguardando conteúdo
            </h3>
            <p class="mt-1 text-xs text-muted-foreground">
              Os proprietários do grupo ainda não adicionaram pastas ou bancos.
            </p>
          </div>
        )
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
        >
          <div class="flex flex-col gap-3 lg:flex-row lg:items-start">
            <aside class="w-full shrink-0 lg:w-64">
              <RepoTreeAside
                folders={folders}
                byParent={byParent}
                isOwner={isOwner}
              />
            </aside>
            <div class="min-w-0 flex-1">
              <RepoMainPane
                items={items}
                byParent={byParent}
                byId={byId}
                isOwner={isOwner}
                groupId={groupId}
              />
            </div>
          </div>
          <DragOverlay dropAnimation={null}>
            {dragLabel ? (
              <div class="pointer-events-none rounded-md border border-violet-500 bg-card px-3 py-1.5 text-xs font-semibold shadow-2xl">
                {dragLabel}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {adding === "folder" && (
        <CreateFolderModal
          groupId={groupId}
          parentId={null}
          onClose={() => setAdding(null)}
          onCreated={() => {
            setAdding(null);
            void loadDetail(groupId);
          }}
        />
      )}
      {adding === "bank" && (
        <AddBankModal
          groupId={groupId}
          parentId={null}
          onClose={() => setAdding(null)}
          onAdded={() => {
            setAdding(null);
            void loadDetail(groupId);
            void loadStudyGroups({ silent: true });
          }}
        />
      )}
    </div>
  );
}

// ============================================================================
// Tree Aside (sidebar de pastas) + Main Pane (conteúdo da pasta selecionada)
// ============================================================================

/** Sidebar à esquerda no Repositório, estilo MeuAcervoExplorer: caixa "PASTAS"
 *  no topo + linha "Meu acervo" violeta (raiz) + linha "Sem pasta (raiz)" pra
 *  bancos diretos + árvore de pastas com badge de contagem. */
function RepoTreeAside({
  folders,
  byParent,
  isOwner
}: {
  folders: StudyGroupItem[];
  byParent: Map<string | null, StudyGroupItem[]>;
  isOwner: boolean;
}) {
  const rootFolders = folders.filter((f) => f.parentId === null);
  const sel = selectedFolderId.value;
  // "Tudo" mostra TODOS os bancos do grupo (recursivo) — igual "Meu acervo".
  // Total = todos os items não-pasta.
  let totalContent = 0;
  let rootContent = 0;
  for (const arr of byParent.values()) {
    for (const i of arr) {
      if (i.kind !== "folder") totalContent++;
    }
  }
  for (const i of byParent.get(null) ?? []) {
    if (i.kind !== "folder") rootContent++;
  }
  return (
    <div class="rounded-lg border border-border bg-card p-2 text-sm">
      <div class="mb-1 px-2 pt-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
        Pastas
      </div>
      <RepoFolderDrop
        id="folder:root"
        isSelected={sel === null}
        onSelect={() => (selectedFolderId.value = null)}
        label="Repositório"
        icon={<Home className="size-4" />}
        count={totalContent}
        highlight
      />
      <RepoFolderDrop
        id="folder:no-folder"
        isSelected={sel === "__no_folder__"}
        onSelect={() => (selectedFolderId.value = "__no_folder__")}
        label="Sem pasta (raiz)"
        icon={<Folder className="size-4 text-amber-500" />}
        count={rootContent}
      />
      {rootFolders.length > 0 && (
        <ul class="mt-0.5 space-y-0.5">
          {rootFolders.map((f) => (
            <RepoFolderNode
              key={f.id}
              folder={f}
              byParent={byParent}
              depth={0}
            />
          ))}
        </ul>
      )}
      {rootFolders.length === 0 && isOwner && (
        <div class="mt-1 px-2 py-1.5 text-[11px] italic text-muted-foreground/70">
          Crie uma pasta pra começar a organizar.
        </div>
      )}
    </div>
  );
}

function RepoFolderNode({
  folder,
  byParent,
  depth
}: {
  folder: StudyGroupItem;
  byParent: Map<string | null, StudyGroupItem[]>;
  depth: number;
}) {
  const expanded = expandedFolders.value.has(folder.id);
  const children = (byParent.get(folder.id) ?? []).filter(
    (i) => i.kind === "folder"
  );
  // Conta só itens NÃO-pasta nessa pasta (recursivo).
  function countContent(fid: string): number {
    let total = 0;
    for (const i of byParent.get(fid) ?? []) {
      if (i.kind === "folder") total += countContent(i.id);
      else total++;
    }
    return total;
  }
  const itemCount = countContent(folder.id);
  const isSelected = selectedFolderId.value === folder.id;

  function toggle(e: MouseEvent) {
    e.stopPropagation();
    const s = new Set(expandedFolders.value);
    if (s.has(folder.id)) s.delete(folder.id);
    else s.add(folder.id);
    expandedFolders.value = s;
  }

  return (
    <li>
      <RepoFolderDrop
        id={`folder:${folder.id}`}
        isSelected={isSelected}
        onSelect={() => (selectedFolderId.value = folder.id)}
        label={folder.name}
        icon={
          children.length > 0 ? (
            <button
              type="button"
              title={expanded ? "Recolher" : "Expandir"}
              aria-label={expanded ? "Recolher pasta" : "Expandir pasta"}
              onClick={toggle}
              class="flex items-center text-muted-foreground hover:text-foreground"
            >
              <ChevronRight
                className={`size-3 transition ${expanded ? "rotate-90" : ""}`}
              />
            </button>
          ) : (
            <span class="inline-block size-3" />
          )
        }
        folderIcon={
          expanded ? (
            <FolderOpen className="size-4 text-amber-500" />
          ) : (
            <Folder className="size-4 text-amber-500" />
          )
        }
        count={itemCount}
        indent={depth}
      />
      {expanded && children.length > 0 && (
        <ul class="space-y-0.5">
          {children.map((c) => (
            <RepoFolderNode
              key={c.id}
              folder={c}
              byParent={byParent}
              depth={depth + 1}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

function RepoFolderDrop({
  id,
  isSelected,
  onSelect,
  label,
  icon,
  folderIcon,
  count,
  indent = 0,
  highlight = false
}: {
  id: string;
  isSelected: boolean;
  onSelect: () => void;
  label: string;
  icon?: any;
  folderIcon?: any;
  count: number;
  indent?: number;
  highlight?: boolean;
}) {
  const { isOver, setNodeRef } = useDroppable({ id });
  const selectedCls = highlight
    ? "bg-violet-600 text-white"
    : "bg-violet-500/15 text-violet-700 dark:text-violet-300";
  return (
    <div
      ref={setNodeRef}
      onClick={onSelect}
      class={`flex cursor-pointer items-center gap-1.5 rounded-md px-2 py-1.5 text-xs transition ${
        isSelected ? selectedCls : "text-foreground hover:bg-accent"
      } ${isOver ? "ring-2 ring-violet-500 ring-offset-1 ring-offset-card" : ""}`}
      style={{ paddingLeft: `${0.5 + indent * 0.75}rem` }}
    >
      {icon}
      {folderIcon}
      <span class="min-w-0 flex-1 truncate font-medium">{label}</span>
      {count > 0 && (
        <span
          class={`shrink-0 rounded-full px-1.5 text-[10px] font-semibold ${
            isSelected && highlight
              ? "bg-white/25 text-white"
              : "bg-muted text-muted-foreground"
          }`}
        >
          {count}
        </span>
      )}
    </div>
  );
}

/** Pane principal, estilo MeuAcervoExplorer:
 *  - Header com breadcrumb + título grande + subtítulo (N subpastas · N bancos)
 *  - Seção "PASTAS (N)" com cards grandes de subpastas (clicáveis)
 *  - Seção "BANCOS" com lista de bancos da pasta selecionada
 *
 *  Filtros especiais:
 *   - selectedFolderId === null → "Repositório" (todos os bancos, recursivo)
 *   - selectedFolderId === "__no_folder__" → bancos diretos na raiz
 *   - selectedFolderId === id → bancos da pasta + subpastas */
function RepoMainPane({
  items,
  byParent,
  byId,
  isOwner,
  groupId
}: {
  items: StudyGroupItem[];
  byParent: Map<string | null, StudyGroupItem[]>;
  byId: Map<string, StudyGroupItem>;
  isOwner: boolean;
  groupId: string;
}) {
  const sel = selectedFolderId.value;
  const currentFolder =
    sel && sel !== "__no_folder__" ? byId.get(sel) ?? null : null;

  // Resolve itens a mostrar — sempre NÃO-recursivo (cada pasta tem seu conteúdo).
  // Mostrar todos os bancos recursivamente na raiz tira o sentido de organizar
  // por pastas. [[feedback Karol 2026-05-25]]
  let allInScope: StudyGroupItem[];
  if (sel === null) {
    // "Repositório" (raiz) → bancos DIRETOS da raiz (não-recursivo)
    allInScope = (byParent.get(null) ?? []).filter((i) => i.kind !== "folder");
  } else if (sel === "__no_folder__") {
    // "Sem pasta (raiz)" → idem (atalho redundante; mantemos pra UX espelhada
    // ao Meu Acervo).
    allInScope = (byParent.get(null) ?? []).filter((i) => i.kind !== "folder");
  } else {
    // Pasta específica: itens diretos
    allInScope = byParent.get(sel) ?? [];
  }

  // Subpastas:
  //  - Raiz ("Repositório"): mostra as PASTAS-RAIZ como cards (igual o Acervo)
  //  - "Sem pasta (raiz)": vazio (já é a lista flat de bancos da raiz)
  //  - Pasta específica: mostra subpastas diretas
  let subFolders: StudyGroupItem[];
  if (sel === null) {
    subFolders = (byParent.get(null) ?? []).filter((i) => i.kind === "folder");
  } else if (sel === "__no_folder__") {
    subFolders = [];
  } else {
    subFolders = (byParent.get(sel) ?? []).filter((i) => i.kind === "folder");
  }
  const contentItems = allInScope.filter((i) => i.kind !== "folder");

  // Breadcrumb
  const crumbs: StudyGroupItem[] = [];
  let cur: StudyGroupItem | null = currentFolder;
  while (cur) {
    crumbs.unshift(cur);
    cur = cur.parentId ? byId.get(cur.parentId) ?? null : null;
  }

  const title =
    sel === null
      ? "Repositório"
      : sel === "__no_folder__"
        ? "Sem pasta (raiz)"
        : currentFolder?.name ?? "Pasta";
  const subtitleParts: string[] = [];
  if (subFolders.length > 0)
    subtitleParts.push(
      `${subFolders.length} ${subFolders.length === 1 ? "subpasta" : "subpastas"}`
    );
  if (contentItems.length > 0)
    subtitleParts.push(
      `${contentItems.length} ${contentItems.length === 1 ? "banco" : "bancos"}`
    );

  return (
    <div class="space-y-4 rounded-lg border border-border bg-card/40 p-4 sm:p-5">
      {/* Header */}
      <div class="space-y-1">
        <div class="flex items-center gap-1 text-xs text-muted-foreground">
          <button
            type="button"
            title="Voltar à raiz"
            aria-label="Voltar à raiz"
            onClick={() => (selectedFolderId.value = null)}
            class="inline-flex items-center gap-1 hover:text-foreground"
          >
            <Home className="size-3" />
            Repositório
          </button>
          {crumbs.map((c) => (
            <span key={c.id} class="inline-flex items-center gap-1">
              <ChevronRight class="size-3" />
              <button
                type="button"
                title={`Ir para ${c.name}`}
                aria-label={`Ir para ${c.name}`}
                onClick={() => (selectedFolderId.value = c.id)}
                class="truncate hover:text-foreground"
              >
                {c.name}
              </button>
            </span>
          ))}
        </div>
        <h2 class="text-xl font-bold tracking-tight text-foreground">
          {title}
        </h2>
        {subtitleParts.length > 0 && (
          <p class="text-xs text-muted-foreground">
            {subtitleParts.join(" · ")}
          </p>
        )}
      </div>

      {/* PASTAS (subpastas em cards grandes) */}
      {subFolders.length > 0 && (
        <section>
          <div class="mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
            Pastas ({subFolders.length})
          </div>
          <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {subFolders.map((f) => (
              <FolderCard
                key={f.id}
                folder={f}
                byParent={byParent}
                isOwner={isOwner}
                groupId={groupId}
              />
            ))}
          </div>
        </section>
      )}

      {/* BANCOS (e outros conteúdos) */}
      <section>
        {(subFolders.length > 0 || contentItems.length > 0) && (
          <div class="mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
            {sel === null ? "Todos os bancos" : "Bancos"}{" "}
            {contentItems.length > 0 && `(${contentItems.length})`}
          </div>
        )}
        {contentItems.length === 0 ? (
          <div class="rounded-lg border border-dashed border-border bg-card/40 p-6 text-center text-xs italic text-muted-foreground">
            {sel === null
              ? "Nenhum banco neste repositório ainda."
              : "Nenhum banco nessa pasta."}
            {isOwner && " Arraste pra cá ou use os botões acima."}
          </div>
        ) : (
          <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {contentItems.map((it) =>
              it.kind === "bank_ref" ? (
                <BankRichCard
                  key={it.id}
                  item={it}
                  isOwner={isOwner}
                  groupId={groupId}
                />
              ) : (
                <Card key={it.id}>
                  <CardContent class="p-2">
                    <RepoItem
                      item={it}
                      isOwner={isOwner}
                      groupId={groupId}
                    />
                  </CardContent>
                </Card>
              )
            )}
          </div>
        )}
      </section>
    </div>
  );
}

/** Card grande de subpasta (estilo MeuAcervo). Clica → entra. Owner pode
 *  arrastar item pra cima dele pra mover. */
function FolderCard({
  folder,
  byParent,
  isOwner,
  groupId
}: {
  folder: StudyGroupItem;
  byParent: Map<string | null, StudyGroupItem[]>;
  isOwner: boolean;
  groupId: string;
}) {
  function countContent(fid: string): number {
    let total = 0;
    for (const i of byParent.get(fid) ?? []) {
      if (i.kind === "folder") total += countContent(i.id);
      else total++;
    }
    return total;
  }
  const bankCount = countContent(folder.id);
  const subCount = (byParent.get(folder.id) ?? []).filter(
    (i) => i.kind === "folder"
  ).length;
  // Drop target pra arrastar items pra dentro
  const droppable = useDroppable({ id: `folder:${folder.id}` });
  // Draggable só pra owner (reordenar / mover pasta pra outra pasta)
  const draggable = useDraggable({
    id: `item:${folder.id}`,
    disabled: !isOwner
  });
  function setRef(el: HTMLElement | null) {
    draggable.setNodeRef(el);
    droppable.setNodeRef(el);
  }
  // Arrasto só pra owner E com "modo organizar" ligado. Desligado (default),
  // sem listeners e sem touch-action:none → scroll por toque normal no mobile.
  const dragProps =
    isOwner && groupRepoEditMode.value
      ? {
          ...(draggable.attributes as any),
          ...(draggable.listeners as any),
          style: { touchAction: "none" }
        }
      : {};

  return (
    <div
      ref={setRef}
      {...dragProps}
      onClick={() => (selectedFolderId.value = folder.id)}
      class={`group relative cursor-pointer overflow-hidden rounded-xl border border-border bg-card p-4 text-left transition hover:-translate-y-0.5 hover:border-amber-500/60 hover:shadow-md ${
        droppable.isOver ? "ring-2 ring-violet-500" : ""
      } ${draggable.isDragging ? "opacity-40" : ""}`}
    >
      <div class="flex items-start gap-3">
        <span class="grid size-12 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-amber-100 to-amber-200 dark:from-amber-900/40 dark:to-amber-700/40">
          <Folder className="size-6 text-amber-600 dark:text-amber-300" />
        </span>
        <div class="min-w-0 flex-1 pt-1">
          <h3 class="truncate text-sm font-semibold text-foreground">
            {folder.name}
          </h3>
          <p class="mt-0.5 text-xs text-muted-foreground">
            {bankCount > 0
              ? `${bankCount} ${bankCount === 1 ? "banco" : "bancos"}`
              : "sem bancos"}
            {subCount > 0 &&
              ` · ${subCount} ${subCount === 1 ? "subpasta" : "subpastas"}`}
          </p>
        </div>
      </div>
    </div>
  );
}

/** Card rico de banco no Explorer do Grupo, alinhado ao Meu Acervo.
 *  Mostra:
 *   - Nome + N questões + versão
 *   - Bloco "ESTUDO" com % acerto + progresso
 *   - Bloco "PROVA" com tentativas + melhor %
 *  Owner pode arrastar pra mover pra outra pasta. */
function BankRichCard({
  item,
  isOwner,
  groupId
}: {
  item: StudyGroupItem;
  isOwner: boolean;
  groupId: string;
}) {
  const draggable = useDraggable({
    id: `item:${item.id}`,
    disabled: !isOwner
  });
  // Arrasto só pra owner E com "modo organizar" ligado. Desligado (default),
  // sem listeners e sem touch-action:none → scroll por toque normal no mobile.
  const dragProps =
    isOwner && groupRepoEditMode.value
      ? {
          ...(draggable.attributes as any),
          ...(draggable.listeners as any),
          style: { touchAction: "none" }
        }
      : {};
  const meta = item.bankMeta;
  if (!meta || !item.bankId) {
    return null;
  }
  const estudo = meta.estudo;
  const provas = meta.provas;
  const accuracy = estudo?.accuracyPct ?? 0;
  const respondidas = estudo?.respondidas ?? 0;
  const questionCount = meta.questionCount;
  const progressPct =
    questionCount > 0
      ? Math.min(100, Math.round((respondidas / questionCount) * 100))
      : 0;

  async function openBankRef(e: MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    const bankId = item.bankId!;
    const [{ setActiveNav }, { openBank }] = await Promise.all([
      import("./App"),
      import("./BankDetailView")
    ]);
    setActiveNav("bancos");
    openBank(bankId);
  }

  const hasEstudo = estudo && respondidas > 0;
  const hasProvas = provas && provas.total > 0;

  return (
    <div
      ref={draggable.setNodeRef}
      {...dragProps}
      onClick={(e) => void openBankRef(e as unknown as MouseEvent)}
      class={`group relative flex cursor-pointer flex-col gap-2 rounded-lg border border-border bg-card p-3 transition hover:-translate-y-0.5 hover:border-violet-500/60 hover:shadow-md ${
        draggable.isDragging ? "opacity-40" : ""
      }`}
    >
      {/* Header com ícone + nome */}
      <div class="flex items-start gap-2.5">
        <span class="grid size-8 shrink-0 place-items-center rounded-lg bg-violet-500/10 text-violet-600 dark:text-violet-300">
          <FileText className="size-4" />
        </span>
        <div class="min-w-0 flex-1">
          <h4 class="line-clamp-2 text-sm font-semibold leading-tight text-foreground">
            {meta.name}
          </h4>
          <p class="mt-0.5 text-[11px] text-muted-foreground">
            {questionCount} {questionCount === 1 ? "questão" : "questões"}
          </p>
        </div>
        {isOwner && (
          <div onClick={(e) => e.stopPropagation()}>
            <RepoItemMenu
              onRename={async () => {
                const next = await openPrompt({
                  title: "Renomear",
                  message: `Renomear "${meta.name}":`,
                  defaultValue: meta.name,
                  placeholder: "Novo nome"
                });
                if (!next || next === meta.name) return;
                try {
                  await call(
                    `/api/student/study-group-repo?id=${encodeURIComponent(item.id)}`,
                    {
                      method: "PATCH",
                      headers: { "content-type": "application/json" },
                      body: JSON.stringify({ name: next })
                    }
                  );
                  await loadDetail(groupId);
                } catch (err) {
                  void openAlert({
                    title: "Não consegui renomear",
                    message: friendlyError(
                      err instanceof ApiError
                        ? err
                        : new Error(String(err))
                    )
                  });
                }
              }}
              onRemove={async () => {
                const ok = await openConfirm({
                  title: "Remover banco",
                  message: `Remover "${meta.name}" do repositório do grupo?`,
                  confirmLabel: "Remover",
                  danger: true
                });
                if (!ok) return;
                try {
                  await call(
                    `/api/student/study-group-repo?id=${encodeURIComponent(item.id)}`,
                    { method: "DELETE" }
                  );
                  await loadDetail(groupId);
                  await loadStudyGroups({ silent: true });
                } catch (err) {
                  void openAlert({
                    title: "Não consegui remover",
                    message: friendlyError(
                      err instanceof ApiError
                        ? err
                        : new Error(String(err))
                    )
                  });
                }
              }}
              isFolder={false}
              groupId={groupId}
              parentId={null}
            />
          </div>
        )}
      </div>

      {/* Progresso compacto. Se nunca iniciou, mostra "não iniciado" discreto. */}
      {hasEstudo || hasProvas ? (
        <div class="space-y-1.5 border-t border-border pt-2">
          {hasEstudo && (
            <div class="flex items-center gap-2 text-[11px]">
              <BookOpen className="size-3 text-violet-500" />
              <span class="text-muted-foreground">
                <strong class="text-foreground">{accuracy}%</strong> em{" "}
                <strong class="text-foreground">{respondidas}</strong>/
                {questionCount}
              </span>
              <div class="ml-auto h-1 w-12 overflow-hidden rounded-full bg-violet-500/20">
                <div
                  class="h-full bg-violet-500"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
            </div>
          )}
          {hasProvas && (
            <div class="flex items-center gap-2 text-[11px] text-muted-foreground">
              <ClipboardCheck className="size-3 text-amber-500" />
              <span>
                <strong class="text-foreground">{provas!.total}</strong>{" "}
                {provas!.total === 1 ? "tentativa" : "tentativas"} · melhor{" "}
                <strong class="text-foreground">
                  {provas!.melhorPercentual ?? 0}%
                </strong>
              </span>
            </div>
          )}
        </div>
      ) : (
        <div class="border-t border-border pt-2 text-[11px] italic text-muted-foreground">
          Não iniciado
        </div>
      )}
    </div>
  );
}

function RepoItem({
  item,
  isOwner,
  groupId
}: {
  item: StudyGroupItem;
  isOwner: boolean;
  groupId: string;
}) {
  // Pasta funciona como drop target (clicar entra; arrastar pra cá move).
  // Item NÃO-pasta só é draggable.
  const draggable = useDraggable({
    id: `item:${item.id}`,
    disabled: !isOwner
  });
  const droppable = useDroppable({
    id: `folder:${item.id}`,
    disabled: item.kind !== "folder"
  });

  async function remove() {
    const ok = await openConfirm({
      title: "Remover item",
      message: `Remover "${item.name}" do repositório do grupo?`,
      confirmLabel: "Remover",
      danger: true
    });
    if (!ok) return;
    try {
      await call(
        `/api/student/study-group-repo?id=${encodeURIComponent(item.id)}`,
        { method: "DELETE" }
      );
      await loadDetail(groupId);
      await loadStudyGroups({ silent: true });
    } catch (err) {
      void openAlert({
        title: "Não consegui remover",
        message: friendlyError(
          err instanceof ApiError ? err : new Error(String(err))
        )
      });
    }
  }

  async function rename() {
    const next = await openPrompt({
      title: "Renomear",
      message: `Renomear "${item.name}":`,
      defaultValue: item.name,
      placeholder: "Novo nome"
    });
    if (!next || next === item.name) return;
    try {
      await call(
        `/api/student/study-group-repo?id=${encodeURIComponent(item.id)}`,
        {
          method: "PATCH",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ name: next })
        }
      );
      await loadDetail(groupId);
    } catch (err) {
      void openAlert({
        title: "Não consegui renomear",
        message: friendlyError(
          err instanceof ApiError ? err : new Error(String(err))
        )
      });
    }
  }

  // Combina refs (draggable + droppable se for pasta)
  function setRef(el: HTMLElement | null) {
    draggable.setNodeRef(el);
    if (item.kind === "folder") droppable.setNodeRef(el);
  }

  const isDragging = draggable.isDragging;
  const isFolderHover = item.kind === "folder" && droppable.isOver;

  // Arrasto só pra owner E com "modo organizar" ligado. Desligado (default),
  // sem listeners e sem touch-action:none → scroll por toque normal no mobile.
  const dragProps =
    isOwner && groupRepoEditMode.value
      ? {
          ...(draggable.attributes as any),
          ...(draggable.listeners as any),
          style: { touchAction: "none" }
        }
      : {};
  return (
    <div
      ref={setRef}
      {...dragProps}
      class={`group flex items-center gap-2 rounded-md px-2 py-1.5 transition ${
        isDragging ? "opacity-40" : ""
      } ${isFolderHover ? "bg-violet-500/15 ring-2 ring-violet-500" : "hover:bg-accent"} ${
        isOwner ? "cursor-grab active:cursor-grabbing" : ""
      }`}
      onDblClick={() => {
        if (item.kind === "folder") selectedFolderId.value = item.id;
      }}
    >
      <span class="shrink-0">
        <ItemIcon kind={item.kind} />
      </span>

      <div class="min-w-0 flex-1">
        {/* Nome local do item no grupo SEMPRE vence — renomear dentro do
            grupo grava em study_group_items.name, então exibir bankMeta.name
            (nome do banco original) escondia o rename. O nome do banco vira
            o default no insert; a partir daí o grupo manda. */}
        <div class="truncate text-sm">{item.name}</div>
        {item.kind === "bank_ref" && item.bankMeta && (
          <div class="truncate text-xs text-muted-foreground">
            {item.bankMeta.questionCount} questões · v{item.bankMeta.version}
          </div>
        )}
      </div>

      {item.kind === "folder" && (
        <button
          type="button"
          title="Abrir pasta"
          aria-label="Abrir pasta"
          onClick={(e) => {
            e.stopPropagation();
            selectedFolderId.value = item.id;
          }}
          class="shrink-0 text-xs text-muted-foreground transition hover:text-violet-600"
        >
          <ChevronRight className="size-4" />
        </button>
      )}

      {item.kind === "bank_ref" && item.bankId && (
        <button
          type="button"
          title={`Abrir ${item.bankMeta?.name ?? item.name}`}
          aria-label={`Abrir banco`}
          class="shrink-0 rounded-md bg-violet-500/10 px-2 py-1 text-xs font-medium text-violet-600 opacity-0 transition hover:bg-violet-500/20 group-hover:opacity-100 dark:text-violet-300"
          onClick={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            const bankId = item.bankId!;
            // Lazy import pra evitar circular top-level (App ↔ StudyGroups).
            // [[feedback-circular-imports-no-top-level-ref]]
            const [{ setActiveNav }, { openBank }] = await Promise.all([
              import("./App"),
              import("./BankDetailView")
            ]);
            setActiveNav("bancos");
            openBank(bankId);
          }}
        >
          Abrir
        </button>
      )}

      {isOwner && (
        <RepoItemMenu
          onRename={rename}
          onRemove={remove}
          isFolder={item.kind === "folder"}
          groupId={groupId}
          parentId={item.kind === "folder" ? item.id : null}
        />
      )}
    </div>
  );
}

function ItemIcon({ kind }: { kind: StudyGroupItem["kind"] }) {
  if (kind === "bank_ref")
    return <Database className="size-4 text-violet-500" />;
  if (kind === "file")
    return <FileText className="size-4 text-blue-500" />;
  if (kind === "note_ref")
    return <StickyNote className="size-4 text-emerald-500" />;
  if (kind === "exam_ref")
    return <PlayCircle className="size-4 text-orange-500" />;
  return <Folder className="size-4 text-amber-500" />;
}

function RepoItemMenu({
  onRename,
  onRemove,
  isFolder,
  groupId,
  parentId
}: {
  onRename: () => void;
  onRemove: () => void;
  isFolder: boolean;
  groupId: string;
  parentId: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [adding, setAdding] = useState<"folder" | "bank" | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick() {
      setOpen(false);
    }
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [open]);

  return (
    <>
      <div
        class="relative shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => setOpen((s) => !s)}
          class="rounded p-1 text-muted-foreground opacity-0 transition group-hover:opacity-100 hover:bg-accent hover:text-foreground"
        >
          <MoreVertical className="size-4" />
        </button>
        {open && (
          <div class="absolute right-0 top-full z-10 mt-1 w-44 rounded-md border border-border bg-popover py-1 shadow-md">
            {isFolder && (
              <>
                <button
                  onClick={() => {
                    setOpen(false);
                    setAdding("folder");
                  }}
                  class="flex w-full items-center gap-2 px-3 py-1.5 text-sm hover:bg-accent"
                >
                  <FolderPlus className="size-3.5" />
                  Nova subpasta
                </button>
                <button
                  onClick={() => {
                    setOpen(false);
                    setAdding("bank");
                  }}
                  class="flex w-full items-center gap-2 px-3 py-1.5 text-sm hover:bg-accent"
                >
                  <Database className="size-3.5" />
                  Adicionar banco aqui
                </button>
                <div class="my-1 border-t border-border" />
              </>
            )}
            <button
              onClick={() => {
                setOpen(false);
                onRename();
              }}
              class="flex w-full items-center gap-2 px-3 py-1.5 text-sm hover:bg-accent"
            >
              <Edit3 className="size-3.5" />
              Renomear
            </button>
            <button
              onClick={() => {
                setOpen(false);
                onRemove();
              }}
              class="flex w-full items-center gap-2 px-3 py-1.5 text-sm text-red-600 hover:bg-accent dark:text-red-400"
            >
              <Trash2 className="size-3.5" />
              Remover
            </button>
          </div>
        )}
      </div>
      {adding === "folder" && (
        <CreateFolderModal
          groupId={groupId}
          parentId={parentId}
          onClose={() => setAdding(null)}
          onCreated={() => {
            setAdding(null);
            void loadDetail(groupId);
          }}
        />
      )}
      {adding === "bank" && (
        <AddBankModal
          groupId={groupId}
          parentId={parentId}
          onClose={() => setAdding(null)}
          onAdded={() => {
            setAdding(null);
            void loadDetail(groupId);
            void loadStudyGroups({ silent: true });
          }}
        />
      )}
    </>
  );
}

function CreateFolderModal({
  groupId,
  parentId,
  onClose,
  onCreated
}: {
  groupId: string;
  parentId: string | null;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await call(
        `/api/student/study-group-repo?groupId=${encodeURIComponent(groupId)}`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            kind: "folder",
            name: name.trim(),
            parentId
          })
        }
      );
      onCreated();
    } catch (err) {
      void openAlert({
        title: "Não consegui criar a pasta",
        message: friendlyError(
          err instanceof ApiError ? err : new Error(String(err))
        )
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      class="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm sm:items-center"
      onClick={onClose}
    >
      <div
        class="w-full max-w-md rounded-t-2xl bg-card p-6 shadow-xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div class="mb-4 flex items-center justify-between">
          <h2 class="text-lg font-semibold">Nova pasta</h2>
          <button
            onClick={onClose}
            class="text-muted-foreground hover:text-foreground"
          >
            <X className="size-5" />
          </button>
        </div>
        <label class="mb-1 block text-sm font-medium">Nome da pasta</label>
        <input
          type="text"
          maxLength={120}
          value={name}
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
          placeholder="Provas de medicina"
          class="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-violet-500 focus:outline-none"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter") void submit();
          }}
        />
        <div class="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            class="bg-violet-600 hover:bg-violet-700 text-white"
            onClick={() => void submit()}
            disabled={saving || !name.trim()}
          >
            {saving ? (
              <Loader2 className="mr-2 size-4 animate-spin" />
            ) : (
              <FolderPlus className="mr-2 size-4" />
            )}
            Criar
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Add Bank Modal: picker dos bancos do owner (acervo) + Biblioteca
// ============================================================================

interface PickableBank {
  id: string;
  name: string;
  source: "acervo" | "biblioteca";
  visibility?: string;
  totalQuestions?: number;
}

function AddBankModal({
  groupId,
  parentId,
  onClose,
  onAdded
}: {
  groupId: string;
  parentId: string | null;
  onClose: () => void;
  onAdded: () => void;
}) {
  const [banks, setBanks] = useState<PickableBank[] | null>(null);
  const [picked, setPicked] = useState<PickableBank | null>(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        // Acervo do owner
        const banksRes = await call<{ banks: Array<{ id: string; name: string; questionCount?: number }> }>(
          "/api/student/banks"
        );
        const acervo: PickableBank[] = banksRes.banks.map((b) => ({
          id: b.id,
          name: b.name,
          source: "acervo" as const,
          totalQuestions: b.questionCount
        }));
        // Biblioteca pública (best-effort — endpoint pode variar)
        let biblio: PickableBank[] = [];
        try {
          const libRes = await call<{ banks: Array<{ id: string; name: string }> }>(
            "/api/student/discover/banks"
          );
          biblio = libRes.banks.map((b) => ({
            id: b.id,
            name: b.name,
            source: "biblioteca" as const
          }));
        } catch {
          /* biblioteca opcional */
        }
        if (!cancelled) {
          const merged = [...acervo, ...biblio];
          // Dedup por id (banco da biblio que já está no acervo)
          const seen = new Set<string>();
          const out: PickableBank[] = [];
          for (const b of merged) {
            if (seen.has(b.id)) continue;
            seen.add(b.id);
            out.push(b);
          }
          setBanks(out);
        }
      } catch {
        if (!cancelled) setBanks([]);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  async function submit() {
    if (!picked) return;
    setSaving(true);
    try {
      await call(
        `/api/student/study-group-repo?groupId=${encodeURIComponent(groupId)}`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            kind: "bank_ref",
            name: picked.name,
            parentId,
            bankId: picked.id
          })
        }
      );
      onAdded();
    } catch (err) {
      void openAlert({
        title: "Não consegui adicionar o banco",
        message: friendlyError(
          err instanceof ApiError ? err : new Error(String(err))
        )
      });
    } finally {
      setSaving(false);
    }
  }

  const filtered = (banks ?? []).filter((b) =>
    b.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div
      class="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm sm:items-center"
      onClick={onClose}
    >
      <div
        class="flex h-[80vh] w-full max-w-2xl flex-col rounded-t-2xl bg-card p-6 shadow-xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div class="mb-4 flex items-center justify-between">
          <h2 class="text-lg font-semibold">Adicionar banco ao grupo</h2>
          <button
            onClick={onClose}
            class="text-muted-foreground hover:text-foreground"
          >
            <X className="size-5" />
          </button>
        </div>
        <input
          type="text"
          value={search}
          onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
          placeholder="Buscar banco no seu acervo ou na Biblioteca…"
          class="mb-3 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-violet-500 focus:outline-none"
        />
        <div class="flex-1 overflow-y-auto rounded-md border border-border">
          {banks === null ? (
            <div class="p-6 text-center text-sm text-muted-foreground">
              <Loader2 className="mx-auto mb-2 size-5 animate-spin" />
              Carregando…
            </div>
          ) : filtered.length === 0 ? (
            <div class="p-6 text-center text-sm text-muted-foreground">
              Nenhum banco encontrado.
            </div>
          ) : (
            filtered.map((b) => (
              <button
                key={b.id}
                onClick={() => setPicked(b)}
                class={`flex w-full items-center justify-between gap-3 border-b border-border px-3 py-2 text-left text-sm transition last:border-0 hover:bg-accent ${
                  picked?.id === b.id ? "bg-violet-500/10" : ""
                }`}
              >
                <div class="min-w-0 flex-1">
                  <div class="truncate font-medium">{b.name}</div>
                  <div class="text-xs text-muted-foreground">
                    {b.source === "acervo" ? "Meu acervo" : "Biblioteca"}
                    {b.totalQuestions
                      ? ` · ${b.totalQuestions} questões`
                      : ""}
                  </div>
                </div>
                {picked?.id === b.id && (
                  <Check className="size-4 text-violet-500" />
                )}
              </button>
            ))
          )}
        </div>
        <div class="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            class="bg-violet-600 hover:bg-violet-700 text-white"
            onClick={() => void submit()}
            disabled={saving || !picked}
          >
            {saving ? (
              <Loader2 className="mr-2 size-4 animate-spin" />
            ) : (
              <Plus className="mr-2 size-4" />
            )}
            Adicionar
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Tab: Membros
// ============================================================================

function MembersTab({
  detail,
  groupId
}: {
  detail: StudyGroupDetail;
  groupId: string;
}) {
  const members = membersSignal.value;
  const isOwner = detail.viewerRole === "owner";
  const [inviting, setInviting] = useState(false);

  async function changeRole(
    userId: string,
    action: "promote" | "demote"
  ) {
    try {
      await call(
        `/api/student/study-group-members?groupId=${encodeURIComponent(groupId)}&userId=${encodeURIComponent(userId)}&action=${action}`,
        { method: "PATCH" }
      );
      await loadDetail(groupId);
    } catch (err) {
      void openAlert({
        title: "Não consegui alterar o papel",
        message: friendlyError(
          err instanceof ApiError ? err : new Error(String(err))
        )
      });
    }
  }

  async function removeMember(userId: string, name: string) {
    const ok = await openConfirm({
      title: "Remover membro",
      message: `Remover ${name} do grupo?`,
      confirmLabel: "Remover",
      danger: true
    });
    if (!ok) return;
    try {
      await call(
        `/api/student/study-group-members?groupId=${encodeURIComponent(groupId)}&userId=${encodeURIComponent(userId)}`,
        { method: "DELETE" }
      );
      await loadDetail(groupId);
      await loadStudyGroups({ silent: true });
    } catch (err) {
      void openAlert({
        title: "Não consegui remover",
        message: friendlyError(
          err instanceof ApiError ? err : new Error(String(err))
        )
      });
    }
  }

  async function leaveGroup() {
    const ok = await openConfirm({
      title: "Sair do grupo",
      message: "Sair deste grupo?",
      confirmLabel: "Sair",
      danger: true
    });
    if (!ok) return;
    try {
      await call(
        `/api/student/study-group-members?groupId=${encodeURIComponent(groupId)}&action=leave`,
        { method: "POST" }
      );
      closeStudyGroup();
      await loadStudyGroups({ silent: true });
    } catch (err) {
      void openAlert({
        title: "Não consegui sair",
        message: friendlyError(
          err instanceof ApiError ? err : new Error(String(err))
        )
      });
    }
  }

  async function toggleHideInfo() {
    try {
      await call(
        `/api/student/study-groups?id=${encodeURIComponent(groupId)}`,
        {
          method: "PATCH",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ hideMemberInfo: !detail.hideMemberInfo })
        }
      );
      await loadDetail(groupId);
    } catch (err) {
      void openAlert({
        title: "Não consegui atualizar privacidade",
        message: friendlyError(
          err instanceof ApiError ? err : new Error(String(err))
        )
      });
    }
  }

  async function togglePendingVisibility() {
    const next: "owners" | "all" =
      detail.pendingInvitesVisibility === "all" ? "owners" : "all";
    try {
      await call(
        `/api/student/study-groups?id=${encodeURIComponent(groupId)}`,
        {
          method: "PATCH",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ pendingInvitesVisibility: next })
        }
      );
      await loadDetail(groupId);
    } catch (err) {
      void openAlert({
        title: "Não consegui atualizar visibilidade dos convites",
        message: friendlyError(
          err instanceof ApiError ? err : new Error(String(err))
        )
      });
    }
  }

  async function cancelPendingInvite(inviteId: string, inviteeName: string) {
    const ok = await openConfirm({
      title: "Cancelar convite",
      message: `Cancelar o convite enviado pra ${inviteeName}? A pessoa ainda pode ser convidada de novo depois.`,
      confirmLabel: "Cancelar convite",
      danger: true
    });
    if (!ok) return;
    try {
      await call(
        `/api/student/study-group-invites?id=${encodeURIComponent(inviteId)}&action=cancel`,
        { method: "PATCH" }
      );
      await loadDetail(groupId);
    } catch (err) {
      void openAlert({
        title: "Não consegui cancelar",
        message: friendlyError(
          err instanceof ApiError ? err : new Error(String(err))
        )
      });
    }
  }

  async function requestConnection(targetUserId: string) {
    try {
      await call(`/api/student/connections`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ addresseeUserId: targetUserId })
      });
      await loadDetail(groupId);
    } catch (err) {
      void openAlert({
        title: "Não consegui enviar pedido",
        message: friendlyError(
          err instanceof ApiError ? err : new Error(String(err))
        )
      });
    }
  }

  return (
    <div class="space-y-3">
      <div class="flex flex-wrap gap-2">
        {isOwner && (
          <Button
            size="sm"
            class="bg-violet-600 hover:bg-violet-700 text-white"
            onClick={() => setInviting(true)}
          >
            <UserPlus className="mr-2 size-4" />
            Convidar pessoa
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={() => void leaveGroup()}>
          <X className="mr-2 size-4" />
          Sair do grupo
        </Button>
      </div>

      {isOwner && (
        <div class="flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-3 py-2.5 text-xs">
          <div class="flex items-start gap-2">
            {detail.hideMemberInfo ? (
              <EyeOff className="mt-0.5 size-4 text-amber-600 dark:text-amber-400" />
            ) : (
              <Eye className="mt-0.5 size-4 text-violet-500" />
            )}
            <div>
              <div class="font-medium text-foreground">
                Privacidade dos membros
              </div>
              <div class="text-muted-foreground">
                {detail.hideMemberInfo
                  ? "Membros não-proprietários veem apenas iniciais e papel. Nome e email ficam ocultos."
                  : "Todos os membros veem nome e email uns dos outros."}
              </div>
            </div>
          </div>
          <button
            type="button"
            title={
              detail.hideMemberInfo ? "Liberar informações" : "Ocultar informações"
            }
            aria-label={
              detail.hideMemberInfo ? "Liberar informações" : "Ocultar informações"
            }
            onClick={() => void toggleHideInfo()}
            class={`shrink-0 rounded-full px-3 py-1 text-xs font-medium transition ${
              detail.hideMemberInfo
                ? "bg-amber-500/15 text-amber-700 hover:bg-amber-500/25 dark:text-amber-300"
                : "bg-muted text-muted-foreground hover:bg-violet-500/15 hover:text-violet-700"
            }`}
          >
            {detail.hideMemberInfo ? "Ocultando" : "Visível"}
          </button>
        </div>
      )}

      {/* === Toggle de visibilidade dos convites pendentes (owner-only) ===
          [[project-study-groups-pending-invites]] */}
      {isOwner && (
        <div class="flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-3 py-2.5 text-xs">
          <div class="flex items-start gap-2">
            {detail.pendingInvitesVisibility === "all" ? (
              <Eye className="mt-0.5 size-4 text-violet-500" />
            ) : (
              <EyeOff className="mt-0.5 size-4 text-amber-600 dark:text-amber-400" />
            )}
            <div>
              <div class="font-medium text-foreground">
                Convites pendentes
              </div>
              <div class="text-muted-foreground">
                {detail.pendingInvitesVisibility === "all"
                  ? "Todos os membros veem quem foi convidado e ainda não aceitou."
                  : "Só proprietários veem a lista de convites pendentes."}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => void togglePendingVisibility()}
            class={`shrink-0 rounded-full px-3 py-1 text-xs font-medium transition ${
              detail.pendingInvitesVisibility === "all"
                ? "bg-violet-500/15 text-violet-700 hover:bg-violet-500/25 dark:text-violet-300"
                : "bg-amber-500/15 text-amber-700 hover:bg-amber-500/25 dark:text-amber-300"
            }`}
          >
            {detail.pendingInvitesVisibility === "all" ? "Todos veem" : "Só proprietários"}
          </button>
        </div>
      )}

      {/* === Lista de convites pendentes ===
          - Owners: sempre veem; podem cancelar
          - Membros: só veem se pendingInvitesVisibility='all' (backend filtra) */}
      {pendingInvitesSignal.value.length > 0 && (
        <Card>
          <CardContent class="space-y-2 p-3">
            <div class="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              <UserPlus className="size-3.5" />
              Convites pendentes ({pendingInvitesSignal.value.length})
            </div>
            <ul class="space-y-1.5">
              {pendingInvitesSignal.value.map((inv) => (
                <li
                  key={inv.id}
                  class="flex items-center justify-between gap-3 rounded-md border border-border bg-background/50 px-3 py-2"
                >
                  <div class="min-w-0 flex-1">
                    <div class="truncate text-sm font-medium">
                      {inv.inviteeName}
                    </div>
                    <div class="truncate text-[11px] text-muted-foreground">
                      {inv.inviteeEmail
                        ? `${inv.inviteeEmail} · `
                        : ""}
                      enviado{" "}
                      {new Date(inv.createdAt).toLocaleDateString("pt-BR")}
                      {inv.invitedByName ? ` por ${inv.invitedByName}` : ""}
                    </div>
                    {inv.message && (
                      <div class="mt-1 truncate text-[11px] italic text-muted-foreground">
                        "{inv.message}"
                      </div>
                    )}
                  </div>
                  {pendingInvitesCanCancel.value && (
                    <button
                      type="button"
                      onClick={() =>
                        void cancelPendingInvite(inv.id, inv.inviteeName)
                      }
                      class="shrink-0 rounded-md border border-destructive/30 bg-destructive/5 px-2.5 py-1 text-[11px] font-medium text-destructive transition-colors hover:bg-destructive/15"
                    >
                      <X className="mr-1 inline size-3" />
                      Cancelar
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent class="p-0">
          {members.map((m, idx) => {
            const isLast = idx === members.length - 1;
            return (
              <div
                key={m.userId}
                class={`flex items-center justify-between gap-3 px-3 py-2 ${
                  isLast ? "" : "border-b border-border"
                }`}
              >
                <div class="flex min-w-0 flex-1 items-center gap-2.5">
                  <div
                    class={`grid size-9 shrink-0 place-items-center rounded-full text-[10px] font-bold text-white ${avatarBg(m.userId)}`}
                    title={m.name}
                    aria-label={m.name}
                  >
                    {memberInitials(m.name)}
                  </div>
                  <div class="min-w-0 flex-1">
                    <div class="flex items-center gap-2">
                      <span class="truncate font-medium">{m.name}</span>
                      {m.isSelf && (
                        <span class="text-[10px] text-muted-foreground">
                          (você)
                        </span>
                      )}
                      {m.role === "owner" && (
                        <Badge variant="secondary" class="gap-1">
                          <Crown className="size-3" />
                          Proprietário
                        </Badge>
                      )}
                      {isOwner && m.hiddenForMembers && (
                        <span
                          class="inline-flex items-center gap-0.5 text-[10px] text-amber-600 dark:text-amber-400"
                          title="Oculto para outros membros"
                        >
                          <EyeOff className="size-3" />
                        </span>
                      )}
                    </div>
                    {m.email && (
                      <div class="truncate text-xs text-muted-foreground">
                        {m.email}
                      </div>
                    )}
                  </div>
                </div>
                <div class="flex shrink-0 items-center gap-1">
                  {!m.isSelf && (
                    <ConnectionButton
                      status={m.connectionStatus}
                      onConnect={() => void requestConnection(m.userId)}
                    />
                  )}
                  {isOwner && !m.isSelf && (
                    <>
                      {m.role === "member" ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => void changeRole(m.userId, "promote")}
                          title="Promover a proprietário"
                          aria-label="Promover a proprietário"
                        >
                          <Crown className="size-3.5" />
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => void changeRole(m.userId, "demote")}
                          title="Rebaixar a membro"
                          aria-label="Rebaixar a membro"
                        >
                          Rebaixar
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        class="text-red-600 hover:text-red-700 dark:text-red-400"
                        onClick={() => void removeMember(m.userId, m.name)}
                        title="Remover do grupo"
                        aria-label="Remover do grupo"
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {inviting && (
        <InviteModal
          groupId={groupId}
          onClose={() => setInviting(false)}
          onInvited={() => {
            setInviting(false);
            void loadDetail(groupId);
          }}
        />
      )}
    </div>
  );
}

/** Botão de conexão entre dois alunos. Reaproveita /api/student/connections.
 *  Estados: sem conexão → "Conectar"; conexão enviada → "Pendente";
 *  recebida → "Aceitar"; aceita → "Conectado" (com ícone). */
function ConnectionButton({
  status,
  onConnect
}: {
  status: StudyGroupMember["connectionStatus"];
  onConnect: () => void;
}) {
  if (status === "accepted") {
    return (
      <span
        class="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2.5 py-1 text-[11px] font-medium text-emerald-700 dark:text-emerald-300"
        title="Vocês estão conectados"
        aria-label="Conectados"
      >
        <Check className="size-3" />
        Conectado
      </span>
    );
  }
  if (status === "pending_sent") {
    return (
      <span
        class="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-[11px] font-medium text-muted-foreground"
        title="Pedido enviado"
        aria-label="Conexão pendente"
      >
        <Loader2 className="size-3" />
        Pendente
      </span>
    );
  }
  if (status === "pending_received") {
    return (
      <a
        href="/student/?section=conexoes"
        class="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2.5 py-1 text-[11px] font-medium text-amber-700 hover:bg-amber-500/25 dark:text-amber-300"
        title="Você tem um pedido pra aceitar"
        aria-label="Aceitar conexão"
        onClick={(e) => e.stopPropagation()}
      >
        <UserPlus className="size-3" />
        Aceitar
      </a>
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onConnect();
      }}
      title="Pedir conexão"
      aria-label="Pedir conexão"
      class="inline-flex items-center gap-1 rounded-full bg-violet-500/10 px-2.5 py-1 text-[11px] font-medium text-violet-700 transition hover:bg-violet-500/20 dark:text-violet-300"
    >
      <UserPlus className="size-3" />
      Conectar
    </button>
  );
}

function InviteModal({
  groupId,
  onClose,
  onInvited
}: {
  groupId: string;
  onClose: () => void;
  onInvited: () => void;
}) {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  async function submit() {
    const e = email.trim().toLowerCase();
    if (!e) return;
    setSaving(true);
    setResult(null);
    try {
      const res = await call<{
        invite?: { id: string };
        alreadyPending?: boolean;
        message?: string;
      }>(`/api/student/study-group-invites?groupId=${encodeURIComponent(groupId)}`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          email: e,
          message: message.trim() || null
        })
      });
      if (res.alreadyPending) {
        setResult("Essa pessoa já tem um convite pendente.");
      } else {
        setResult("Convite enviado! A pessoa vai receber uma notificação.");
        setEmail("");
        setMessage("");
        onInvited();
      }
    } catch (err) {
      const friendly = friendlyError(
        err instanceof ApiError ? err : new Error(String(err))
      );
      setResult(friendly);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      class="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm sm:items-center"
      onClick={onClose}
    >
      <div
        class="w-full max-w-md rounded-t-2xl bg-card p-6 shadow-xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div class="mb-4 flex items-center justify-between">
          <h2 class="text-lg font-semibold">Convidar pessoa</h2>
          <button
            onClick={onClose}
            class="text-muted-foreground hover:text-foreground"
          >
            <X className="size-5" />
          </button>
        </div>
        <p class="mb-4 text-xs text-muted-foreground">
          A pessoa precisa estar cadastrada. Ela vai receber uma notificação no
          painel pra aceitar ou recusar.
        </p>
        <div class="space-y-3">
          <div>
            <label class="mb-1 block text-sm font-medium">Email</label>
            <input
              type="email"
              value={email}
              onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
              placeholder="aluno@exemplo.com"
              class="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-violet-500 focus:outline-none"
              autoFocus
            />
          </div>
          <div>
            <label class="mb-1 block text-sm font-medium">
              Mensagem (opcional)
            </label>
            <textarea
              maxLength={300}
              rows={2}
              value={message}
              onInput={(e) =>
                setMessage((e.target as HTMLTextAreaElement).value)
              }
              placeholder="Vem estudar com a gente!"
              class="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-violet-500 focus:outline-none"
            />
          </div>
        </div>
        {result && (
          <div class="mt-3 rounded-md bg-violet-500/10 px-3 py-2 text-xs text-violet-600 dark:text-violet-300">
            {result}
          </div>
        )}
        <div class="mt-6 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Fechar
          </Button>
          <Button
            class="bg-violet-600 hover:bg-violet-700 text-white"
            onClick={() => void submit()}
            disabled={saving || !email.trim()}
          >
            {saving ? (
              <Loader2 className="mr-2 size-4 animate-spin" />
            ) : (
              <Mail className="mr-2 size-4" />
            )}
            Enviar convite
          </Button>
        </div>
      </div>
    </div>
  );
}
